/**
 * t437u: pre-connect calls on the web. While a call rings (Accept not yet pressed), from the moment Call is pressed (in
 * parallel with POST /calls), and while a chat, person sheet or project with a Call button is open, this:
 * - loads the call screen's code (LiveKit is ~700 kB and was only fetched after the join answer), and
 * - runs LiveKit's room.prepareConnection(url) on the URL from GET /me, so DNS + TLS to livekit.thehubapp.co.uk are
 *   done before room.connect().
 * hub-lon runs LiveKit on 3 nodes behind one round-robin name (Redis routes each room's signalling to the node that
 * hosts it), so the warm-up is to the shared hostname; no per-node URL exists for a client to target.
 * Fire and forget: never throws, never delays a call; failures are debug-logged only.
 */
import { useEffect } from 'react';
import { createCallPreparer, type Me } from '@tradeoutlet/shared';
import { qk, queryClient } from '@/lib/queryClient';

const LOGGER = 'hub-call-prepare';
const debug = (msg: string, err?: unknown) => { if (import.meta.env.DEV) console.debug(msg, err ?? ''); };

let chunk: Promise<unknown> | null = null;
/** The call screen's code (and LiveKit with it), loaded once; a failed load is tried again next time. */
export function preloadCallRoom(): Promise<unknown> {
  if (!chunk) {
    chunk = import('./CallRoom').catch(e => { chunk = null; debug('call: preload failed (ignored)', e); });
  }
  return chunk;
}

export const callPreparer = createCallPreparer({
  debug,
  async room() {
    const lk = await import('livekit-client');
    // A Room of its own that never connects, with its SDK logs silenced: a failed warm-up is not worth a console warning.
    const r = new lk.Room({ loggerName: LOGGER } as ConstructorParameters<typeof lk.Room>[0]);
    lk.setLogLevel('silent', LOGGER as Parameters<typeof lk.setLogLevel>[1]);
    return r;
  },
});

/** Warm up for a call about to happen. Safe to call often (throttled); returns at once. */
export function prepareCall(url?: string | null): void {
  try {
    void preloadCallRoom();
    // The URL from GET /me (already in the cache by the time any call UI shows).
    void callPreparer.prepare(url ?? callPreparer.url() ?? queryClient.getQueryData<Me>(qk.me)?.livekit_url);
  } catch (e) { debug('call: prepare failed (ignored)', e); }
}

/** A moment after it opens, so the screen's own data and images load first. */
const SCREEN_DELAY_MS = 1_500;

/** Warm up while a screen with a Call button is open (again for each `key`, e.g. the person shown). */
export function usePrepareCall(enabled = true, key?: string | null): void {
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => prepareCall(), SCREEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled, key]);
}
